<script setup lang="ts">import { ref as _ref } from 'vue';

import { createStore, renderWebChat } from "botframework-webchat";
import { inject, onBeforeUnmount, onMounted, useTemplateRef } from "vue";

import { config } from "@/config";
import { createAgentAssistantConnection } from "@/hooks/use-agent-assistant-conversation";
import { useAllCasePanels } from "@/hooks/use-case-panel";

import type { CopilotStudioWebChatConnection } from "@microsoft/agents-copilotstudio-client";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";
import { useCaseStore } from "@/stores";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const allPanels = useAllCasePanels();

// WebChat injects its own <style> bundle at mount time; without the site's CSP nonce it gets
// blocked by the style-src directive and the widget renders unstyled.
const nonce = inject<string>("NONCE");

const webchatEl = useTemplateRef<HTMLDivElement>("webchat");

let connection: CopilotStudioWebChatConnection | undefined;
let seededConversationId: string | undefined;
// Guards LinkViewerSession firing more than once per panel instance — kept
// separate from seededConversationId because a WebChat reconnect can start
// a genuinely new underlying conversation (a different id), which the
// conversationId dedupe below wouldn't catch, but we still only want to
// link once.
let sessionLinked = false;
let error = _ref<string | null>(null);

// CopilotStudioWebChat.createConnection only sends a single 'typing' activity per turn, right
// when the message is posted — it never repeats it while the agent is still working. WebChat's
// typing indicator expires TYPING_ANIMATION_DURATION_MS after the last 'typing' activity it saw,
// so without a repeat, the indicator vanishes long before a multi-tool-call turn finishes and the
// panel looks dead. Re-dispatching our own 'typing' activity on an interval keeps it alive until
// the real response arrives — the interval must stay shorter than TYPING_ANIMATION_DURATION_MS
// (passed to renderWebChat below) or each ping arrives right as the previous one is expiring,
// causing a visible flicker/pause rather than a continuous animation. Once the real reply lands
// we stop pinging, but the indicator still lingers until TYPING_ANIMATION_DURATION_MS has passed
// since the last ping — so that gap is kept small (not just "comfortable") to minimize how long
// the indicator hangs around after the agent has actually finished. Pings are dispatched locally
// to the store rather than over the network, so their timing is precise enough that a small gap
// doesn't risk flicker.
const TYPING_PING_INTERVAL_MS = 1200;
const TYPING_ANIMATION_DURATION_MS = 1350;
let typingIntervalId: ReturnType<typeof setInterval> | undefined;

function stopTypingPing() {
  if (typingIntervalId != null) {
    clearInterval(typingIntervalId);
    typingIntervalId = undefined;
  }
}

// connection.activity$ is a cold Observable, and CopilotStudioWebChat.createConnection's
// executor only streams real activities to whichever subscriber is subscribed *after* the
// connection status has already flipped to Connected — WebChat's own internal saga already
// juggles that "prime, then re-subscribe" handshake correctly. A second, independent
// `connection.activity$.subscribe(...)` here would race that handshake and could steal or
// duplicate the subscription (verified against botframework-webchat-core's compiled source:
// its `mu`/connect saga does a throwaway subscribe-then-unsubscribe purely to flip
// connectionStatus$, then a *second* real subscribe is what actually calls
// client.startConversationAsync()). Reading the conversation id off the
// DIRECT_LINE/INCOMING_ACTIVITY action WebChat's store dispatches avoids adding another
// subscriber entirely — it just observes what WebChat's own single canonical subscription
// already received.
function createWebChatStore() {
  return createStore({}, (store) => (next: (action: unknown) => unknown) => (action: unknown) => {
    if (typeof action !== "object" || action == null || !("type" in action)) {
      return next(action);
    }

    if (action.type === "DIRECT_LINE/POST_ACTIVITY") {
      stopTypingPing();

      const dispatchTyping = () =>
        store.dispatch({
          type: "DIRECT_LINE/INCOMING_ACTIVITY",
          payload: {
            activity: { type: "typing", from: { id: "agent", name: "Agent" }, timestamp: new Date().toISOString() },
          },
        });

      dispatchTyping();
      typingIntervalId = setInterval(dispatchTyping, TYPING_PING_INTERVAL_MS);
    }

    if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY" && "payload" in action) {
      const activity = (
        action as {
          payload: { activity?: { type?: string; from?: { role?: string }; conversation?: { id?: string } } };
        }
      ).payload.activity;

      // CopilotStudioWebChat's postActivity echoes the just-sent user message back through the
      // same activity stream (so WebChat can render it) before the agent's real reply arrives —
      // that echo is also type 'message', tagged with from.role 'user' by WebChat itself. Only a
      // non-user message activity is an actual agent reply, so only that should stop the pings.
      if (activity?.type === "message" && activity.from?.role !== "user") {
        stopTypingPing();
      }

      const conversationId = activity?.conversation?.id;

      if (conversationId != null && conversationId !== seededConversationId) {
        seededConversationId = conversationId;
      }

      if (conversationId != null && !sessionLinked) {
        sessionLinked = true;

        // Fired here rather than off connectionStatus$ === Connected — that
        // status flips via a throwaway subscribe/unsubscribe WebChat does
        // internally to track state, independently of when the real
        // conversation (and startConversationAsync()) actually goes live
        // (see the comment above this function). Posting from there raced
        // that: it usually lost, since the real conversation often wasn't
        // live yet, and only "worked" with a debugger pause because that
        // incidentally gave the race enough time. This first real incoming
        // activity can only happen once the conversation is genuinely live,
        // so there's nothing left to race.
        setTimeout(() => {
          const { sessionLinkId } = useCaseStore();
          connection
            ?.postActivity({
              from: { id: "web-user", name: "web-user" },
              type: "event",
              name: "LinkViewerSession",
              // Sent as the raw value rather than a wrapped object — matches
              // a Copilot Studio custom-event trigger binding a topic
              // variable straight off System.Activity.Value (a scalar),
              // rather than needing an exact-cased nested field name.
              value: sessionLinkId,
            } as Parameters<CopilotStudioWebChatConnection["postActivity"]>[0])
            .subscribe(
              (id: string) => console.log("Successfully seeded variables with activity ID:", id),
              (error: any) => console.error("Failed to send context:", error),
            );
        }, 0);
      }
    }

    return next(action);
  });
}

onMounted(async () => {
  // CaseFixed briefly mounts every panel component on initial load, even ones whose tab is
  // hidden, so that panels needing that mount-time setup get it. Agent Assistant doesn't need
  // that — connecting is expensive and shouldn't happen for a panel the user can't see.
  if (!allPanels.value.agentAssistant.enabled) return;

  if (config.agent == null) {
    error.value = "Agent Assistant is not configured for this environment.";
    return;
  }

  try {
    connection = await createAgentAssistantConnection();

    if (webchatEl.value != null) {
      renderWebChat(
        {
          directLine: connection,
          store: createWebChatStore(),
          nonce,
          styleOptions: { typingAnimationDuration: TYPING_ANIMATION_DURATION_MS },
        },
        webchatEl.value,
      );
    }
  } catch (err) {
    error.value = "Failed to start the agent conversation.";
    console.error(error.value, err);
  }
});

onBeforeUnmount(() => {
  stopTypingPing();
  connection?.end();
});
</script>

<template>
  <div class="tw-flex tw-size-full tw-flex-col">
    <h2 class="tw-bg-gray-900 tw-p-2 tw-text-base tw-font-semibold tw-text-white">Agent Assistant</h2>
    <div v-if="error" class="tw-p-4 tw-text-sm tw-text-red-700">{{ error }}</div>
    <div ref="webchat" class="tw-min-h-0 tw-grow tw-overflow-hidden" role="main"></div>
  </div>
</template>
